import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { useCloudflareStorage } from '@/hooks/useCloudflareStorage';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription, 
} from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage, FormDescription } from '@/components/ui/form';
import { Loader2, Upload } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';

const formSchema = z.object({
  title: z.string().min(1, 'El título es requerido'),
  description: z.string().min(1, 'La descripción es requerida'),
  category: z.string().min(1, 'La categoría es requerida'),
  isPremium: z.boolean().default(false),
  coverImage: z.string().min(1, 'La imagen de portada es requerida'),
  fileUrl: z.string().min(1, 'El archivo PDF es requerido'),
});

type FormData = z.infer<typeof formSchema>;

interface CreatePDFDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreatePDFDialog: React.FC<CreatePDFDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const { createMaterial } = useDigitalMaterials();
  const { uploadFile } = useCloudflareStorage();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      category: '',
      isPremium: false,
      coverImage: '',
      fileUrl: '',
    },
    mode: 'onChange',
  });

  const handleFileUpload = async (file: File, field: 'coverImage' | 'fileUrl') => {
    try {
      setIsUploading(true);
      const type = field === 'coverImage' ? 'IMAGE' : 'DOCUMENT';
      
      if (field === 'fileUrl' && !file.type.includes('pdf')) {
        throw new Error('Solo se permiten archivos PDF');
      }
      
      const fileUrl = await uploadFile(file, type);
      form.setValue(field, fileUrl);
      
      toast({
        title: 'Archivo subido exitosamente',
        description: `El ${field === 'coverImage' ? 'imagen' : 'PDF'} se ha subido correctamente.`,
      });
    } catch (error) {
      console.error('Error al subir archivo:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: error instanceof Error ? error.message : "No se pudo subir el archivo. Por favor, intente nuevamente.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const onSubmit = async (values: FormData) => {
    try {
      if (isUploading) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Por favor, espera a que se complete la subida de archivos.",
        });
        return;
      }

      const material = {
        ...values,
        type: 'PDF' as const,
        content: '', // Campo requerido para la base de datos
        isDownloadable: true,
        tags: [], // Array vacío por defecto
      };

      await createMaterial(material);
      
      toast({
        title: "PDF creado",
        description: "El material PDF se ha creado exitosamente.",
      });
      
      form.reset();
      onOpenChange(false);
    } catch (error) {
      console.error('Error creating PDF:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: "No se pudo crear el PDF. Por favor, verifica todos los campos e intenta nuevamente.",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Subir nuevo PDF</DialogTitle>
          <DialogDescription>
            Sube un archivo PDF y completa la información necesaria para agregarlo a la biblioteca.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Título</FormLabel>
                  <FormControl>
                    <Input placeholder="Título del PDF" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripción</FormLabel>
                  <FormControl>
                    <Input placeholder="Breve descripción del PDF" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Categoría</FormLabel>
                  <FormControl>
                    <Input 
                      placeholder="Ej: Finanzas Básicas, Ahorro, Inversiones" 
                      {...field} 
                    />
                  </FormControl>
                  <FormDescription>
                    Escribe una categoría nueva o usa una existente
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <Label>Imagen de portada</Label>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'coverImage');
                  }}
                />
                {form.formState.errors.coverImage && (
                  <p className="text-sm text-red-500">
                    {form.formState.errors.coverImage.message}
                  </p>
                )}
              </div>

              <div className="space-y-4">
                <Label>Archivo PDF</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleFileUpload(file, 'fileUrl');
                  }}
                />
                {form.formState.errors.fileUrl && (
                  <p className="text-sm text-red-500">
                    {form.formState.errors.fileUrl.message}
                  </p>
                )}
              </div>
            </div>

            <FormField
              control={form.control}
              name="isPremium"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Contenido Premium</FormLabel>
                    <FormDescription>
                      Marca esta opción si el contenido es exclusivo para usuarios premium
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                Cancelar
              </Button>
              <Button 
                type="submit"
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Subiendo archivos...
                  </>
                ) : (
                  'Crear PDF'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
